/**
 * Thin wrappers around the orval-generated Data refinery hooks: the
 * benchmarks the team's evals config names, and one benchmark's tree of
 * the latest scoring night's tests with the proven tasks generated from
 * them (services/refinery_tree.py). Read-only. Same unwrap pattern as
 * useProvenTasks.ts, which keeps the case-file reads a task row opens.
 */

import type { ApiRequestError } from '@/api/client'
import {
  useListRefineryBenchmarksApiProvenTasksBenchmarksGet,
  useReadRefineryTreeApiProvenTasksTreeGet,
} from '@/api/generated/proven-tasks/proven-tasks'
import type { RefineryBenchmarksResponse, RefineryTreeResponse } from '@/api/generated/model'

interface UnwrappedQuery<TData> {
  data: TData | undefined
  isLoading: boolean
  error: ApiRequestError | null
  isError: boolean
}

export function useRefineryBenchmarks(): UnwrappedQuery<RefineryBenchmarksResponse> {
  const query = useListRefineryBenchmarksApiProvenTasksBenchmarksGet()
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}

/** The tree of one benchmark; nothing is read until the page knows which. */
export function useRefineryTree(benchmark: string | undefined): UnwrappedQuery<RefineryTreeResponse> {
  const enabled = benchmark !== undefined
  const query = useReadRefineryTreeApiProvenTasksTreeGet(
    { benchmark: benchmark ?? '' },
    { query: { enabled, retry: false } },
  )
  return {
    data: query.data?.status === 200 ? query.data.data : undefined,
    isLoading: enabled && query.isLoading,
    error: query.error ?? null,
    isError: query.isError,
  }
}
